/**
 * SolSazón — Capa de analítica unificada
 *
 * Arquitectura:
 *  - FRONTEND: consent-gated dataLayer + direct GA4 initialized by analyticsConsent
 *  - NO hay Measurement Protocol desde frontend (evita exponer API secrets)
 *  - Si se necesita MP server-side, debe ir en una Supabase Edge Function
 *
 * Anti-duplicados:
 *  - purchase: protegido con sessionStorage (clave por transaction_id)
 *  - view_item: protegido con useRef en el componente (reset al cambiar slug)
 *  - view_item_list: debounce de 300ms para evitar disparos en cada render/filtro
 *
 * No hay console.log en producción (solo console.warn en DEV).
 */

import { analyticsPageContext, canCollectAnalytics, initializeAnalytics, isPrivateAnalyticsPath } from './analyticsConsent';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface GAEvent {
  name: string;
  params?: Record<string, string | number | boolean | null | undefined>;
}

/** Item de producto para eventos ecommerce GA4 */
export interface GAItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_name?: string;
  discount?: number;
}

// ─── dataLayer helper ─────────────────────────────────────────────────────────

/**
 * Empuja un evento al dataLayer de GTM.
 * Si GTM no está instalado, el array se crea igualmente y no rompe nada.
 * Cuando GTM esté instalado, leerá este dataLayer automáticamente para
 * enviar a GA4, Meta Pixel, TikTok Pixel, etc.
 */
function pushDataLayer(event: Record<string, unknown>): void {
  if (!canCollectAnalytics()) return;
  try {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ ...event, ...analyticsPageContext() });
    if (import.meta.env.DEV) {
      console.warn('[Analytics dataLayer]', event.event, event);
    }
  } catch {
    // silencioso en producción
  }
}

/**
 * Envía un evento a GA4 via gtag() client-side.
 * Solo funciona si el snippet de GA4 está instalado en index.html.
 * No requiere ningún secret ni variable de entorno.
 */
function sendGtagEvent(eventName: string, params: Record<string, unknown>): void {
  if (!canCollectAnalytics()) return;
  initializeAnalytics();
  try {
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      gtag('event', eventName, { ...params, ...analyticsPageContext() });
    }
  } catch {
    // silencioso
  }
}

/**
 * Punto de entrada unificado: dataLayer + gtag client-side.
 * NO hace ninguna llamada fetch. NO usa API secrets.
 */
export function sendGAEvent(...events: GAEvent[]): void {
  events.forEach((e) => {
    const params = e.params ?? {};
    pushDataLayer({ event: e.name, ...params });
    sendGtagEvent(e.name, params);
  });
}

// ─── Anti-duplicado: purchase ─────────────────────────────────────────────────

const PURCHASE_SESSION_KEY = 'ga_purchase_fired';

/**
 * Devuelve true si el purchase ya fue disparado en esta sesión de navegador.
 * Usa sessionStorage para que una recarga de /order-success no duplique el evento.
 */
function isPurchaseAlreadyFired(transactionId: string): boolean {
  try {
    const fired = sessionStorage.getItem(PURCHASE_SESSION_KEY);
    if (!fired) return false;
    const ids: string[] = JSON.parse(fired);
    return ids.includes(transactionId);
  } catch {
    return false;
  }
}

function markPurchaseAsFired(transactionId: string): void {
  try {
    const fired = sessionStorage.getItem(PURCHASE_SESSION_KEY);
    const ids: string[] = fired ? JSON.parse(fired) : [];
    if (!ids.includes(transactionId)) {
      ids.push(transactionId);
      sessionStorage.setItem(PURCHASE_SESSION_KEY, JSON.stringify(ids));
    }
  } catch {
    // silencioso
  }
}

// ─── Anti-duplicado: view_item_list (debounce) ────────────────────────────────

const viewItemListTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

// ─── Helpers de conveniencia ──────────────────────────────────────────────────

/** page_view — llamar en cada cambio de ruta */
export const gaPageView = (pagePath: string, pageTitle?: string): boolean => {
  const pathname = pagePath.split(/[?#]/)[0];
  if (!canCollectAnalytics() || isPrivateAnalyticsPath(pathname) || pathname === '/checkout') return false;
  sendGAEvent({ name: 'page_view', params: { page_path: pagePath, page_title: pageTitle } });
  return true;
};

/**
 * view_item_list — cuando una lista de productos es visible.
 * Debounce de 300ms: si se llama varias veces seguidas (filtros, rerenders),
 * solo dispara una vez al final.
 */
export const gaViewItemList = (params: {
  list_name: string;
  source_section: string;
  items: GAItem[];
}): void => {
  const key = `${params.list_name}__${params.source_section}`;

  // Cancelar timer anterior si existe
  const existing = viewItemListTimers.get(key);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    viewItemListTimers.delete(key);
    const eventParams = {
      item_list_name: params.list_name,
      source_section: params.source_section,
    };
    pushDataLayer({ event: 'view_item_list', ...eventParams, items: params.items });
    sendGtagEvent('view_item_list', { ...eventParams, items: params.items });
  }, 300);

  viewItemListTimers.set(key, timer);
};

/** select_item — cuando el usuario hace clic en un producto de una lista */
export const gaSelectItem = (params: {
  item: GAItem;
  list_name: string;
  source_section: string;
}): void => {
  const eventParams = {
    item_list_name: params.list_name,
    source_section: params.source_section,
    item_id: params.item.item_id,
    item_name: params.item.item_name,
    item_category: params.item.item_category,
    price: params.item.price,
    index: params.item.index,
  };
  sendGAEvent({ name: 'select_item', params: eventParams });
};

/**
 * view_item — cuando el usuario ve la ficha de un producto.
 * La protección contra rerenders se gestiona con useRef en el componente
 * (ver product-detail/page.tsx: viewItemFired.current).
 */
export const gaViewItem = (params: {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  currency?: string;
  source_section?: string;
}): void => {
  const items: GAItem[] = [{
    item_id: params.item_id,
    item_name: params.item_name,
    item_category: params.item_category,
    price: params.price,
    quantity: 1,
  }];
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.price ?? 0,
    source_section: params.source_section ?? 'product_detail',
  };
  pushDataLayer({ event: 'view_item', ...eventParams, items });
  sendGtagEvent('view_item', { ...eventParams, items });
};

/** add_to_cart — cuando el usuario añade un producto al carrito */
export const gaAddToCart = (params: {
  item_id: string | number;
  item_name: string;
  value: number;
  currency?: string;
  item_category?: string;
  item_variant?: string;
  quantity?: number;
  source_section?: string;
}): void => {
  const qty = params.quantity ?? 1;
  const items: GAItem[] = [{
    item_id: String(params.item_id),
    item_name: params.item_name,
    item_category: params.item_category,
    item_variant: params.item_variant,
    price: params.value / qty,
    quantity: qty,
  }];
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.value,
    source_section: params.source_section ?? 'unknown',
  };
  pushDataLayer({ event: 'add_to_cart', ...eventParams, items });
  sendGtagEvent('add_to_cart', { ...eventParams, items });
};

/** remove_from_cart — cuando el usuario elimina un producto del carrito */
export const gaRemoveFromCart = (params: {
  item_id: string | number;
  item_name: string;
  value: number;
  currency?: string;
  item_category?: string;
  quantity?: number;
}): void => {
  const items: GAItem[] = [{
    item_id: String(params.item_id),
    item_name: params.item_name,
    item_category: params.item_category,
    price: params.value / (params.quantity ?? 1),
    quantity: params.quantity ?? 1,
  }];
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.value,
  };
  pushDataLayer({ event: 'remove_from_cart', ...eventParams, items });
  sendGtagEvent('remove_from_cart', { ...eventParams, items });
};

/** begin_checkout — cuando el usuario inicia el proceso de pago */
export const gaBeginCheckout = (params: {
  value: number;
  currency?: string;
  coupon?: string;
  items: GAItem[];
}): void => {
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.value,
    coupon: params.coupon,
  };
  pushDataLayer({ event: 'begin_checkout', ...eventParams, items: params.items });
  sendGtagEvent('begin_checkout', { ...eventParams, items: params.items });
};

/** add_shipping_info — cuando el usuario selecciona método de envío */
export const gaAddShippingInfo = (params: {
  value: number;
  currency?: string;
  shipping_tier?: string;
  items: GAItem[];
}): void => {
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.value,
    shipping_tier: params.shipping_tier,
  };
  pushDataLayer({ event: 'add_shipping_info', ...eventParams, items: params.items });
  sendGtagEvent('add_shipping_info', { ...eventParams, items: params.items });
};

/** add_payment_info — cuando el usuario selecciona método de pago */
export const gaAddPaymentInfo = (params: {
  value: number;
  currency?: string;
  payment_type?: string;
  items: GAItem[];
}): void => {
  const eventParams = {
    currency: params.currency ?? 'EUR',
    value: params.value,
    payment_type: params.payment_type,
  };
  pushDataLayer({ event: 'add_payment_info', ...eventParams, items: params.items });
  sendGtagEvent('add_payment_info', { ...eventParams, items: params.items });
};

/**
 * purchase is reserved for a verified payment integration.
 * Order creation alone uses order_submitted and never reports paid revenue.
 */
export const gaPurchase = (params: {
  /** Only a server-verified paid order may become purchase revenue. */
  payment_status?: string;
  transaction_id: string;
  value: number;
  currency?: string;
  tax?: number;
  shipping?: number;
  coupon?: string;
  items: GAItem[];
}): void => {
  if (params.payment_status !== 'paid' || !canCollectAnalytics()) return;
  if (isPurchaseAlreadyFired(params.transaction_id)) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] purchase ya disparado para', params.transaction_id, '— ignorado');
    }
    return;
  }

  markPurchaseAsFired(params.transaction_id);

  const eventParams = {
    transaction_id: params.transaction_id,
    value: Math.round(params.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0) * 100) / 100,
    currency: params.currency ?? 'EUR',
    tax: params.tax ?? 0,
    shipping: params.shipping ?? 0,
    coupon: params.coupon,
  };
  pushDataLayer({ event: 'purchase', ...eventParams, items: params.items });
  sendGtagEvent('purchase', { ...eventParams, items: params.items });
};

/** Secondary funnel event: emitted by the successful checkout, not by viewing an order. */
export const gaOrderSubmitted = (params: {
  transaction_id: string;
  currency?: string;
  shipping: number;
  coupon?: string;
  items: GAItem[];
}): void => {
  if (!canCollectAnalytics() || !params.transaction_id) return;
  const key = 'ga_order_submitted_v1';
  let ids: string[] = [];
  try { ids = JSON.parse(sessionStorage.getItem(key) || '[]'); } catch { /* Session-only fallback. */ }
  if (ids.includes(params.transaction_id)) return;
  const eventParams = {
    ...params,
    currency: params.currency || 'EUR',
    value: Math.round(params.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0) * 100) / 100,
  };
  sendGtagEvent('order_submitted', eventParams);
  try { sessionStorage.setItem(key, JSON.stringify([...ids, params.transaction_id])); } catch { /* Checkout remains successful. */ }
};

// ─── Eventos de navegación y conversión ──────────────────────────────────────

/** search — búsqueda interna */
export const gaSearch = (params: {
  search_term: string;
  results_count?: number;
  source?: string;
}): void => {
  sendGAEvent({
    name: 'search',
    params: {
      search_term: params.search_term,
      results_count: params.results_count,
      source: params.source ?? 'search_bar',
    },
  });
};

/** filter_applied — cuando el usuario aplica un filtro en /products */
export const gaFilterApplied = (params: {
  filter_type: string;
  filter_value: string;
  source_section?: string;
}): void => {
  sendGAEvent({
    name: 'filter_applied',
    params: {
      filter_type: params.filter_type,
      filter_value: params.filter_value,
      source_section: params.source_section ?? 'products',
    },
  });
};

/** select_related_product — clic en producto relacionado */
export const gaSelectRelatedProduct = (params: {
  item_id: string | number;
  item_name: string;
  relation_type: 'pairs_with' | 'frequently_bought_together' | 'similar' | 'upsell';
  source_product_id?: string | number;
  source_product_name?: string;
  price?: number;
  index?: number;
}): void => {
  sendGAEvent({
    name: 'select_related_product',
    params: {
      item_id: String(params.item_id),
      item_name: params.item_name,
      relation_type: params.relation_type,
      source_product_id: params.source_product_id ? String(params.source_product_id) : undefined,
      source_product_name: params.source_product_name,
      price: params.price,
      index: params.index,
      source_section: 'related_block',
    },
  });
};

/**
 * select_pack — clic en un pack/bundle.
 * Implementado en: src/pages/bundles/components/BundleCard.tsx
 */
export const gaSelectPack = (params: {
  pack_id: string | number;
  pack_name: string;
  price?: number;
  source_section?: string;
}): void => {
  sendGAEvent({
    name: 'select_pack',
    params: {
      pack_id: String(params.pack_id),
      pack_name: params.pack_name,
      price: params.price,
      source_section: params.source_section ?? 'bundles',
    },
  });
};

/**
 * coupon_applied — cuando el usuario aplica un cupón con éxito.
 * Implementado en: src/components/feature/CouponInput.tsx
 */
export const gaCouponApplied = (params: {
  coupon_code: string;
  discount_value?: number;
  source?: string;
}): void => {
  sendGAEvent({
    name: 'coupon_applied',
    params: {
      coupon_code: params.coupon_code,
      discount_value: params.discount_value,
      source: params.source ?? 'checkout',
    },
  });
};

/** cta_click — clic en CTA principal */
export const gaCtaClick = (params: {
  cta_text: string;
  cta_destination?: string;
  source_section: string;
}): void => {
  sendGAEvent({
    name: 'cta_click',
    params: {
      cta_text: params.cta_text,
      cta_destination: params.cta_destination,
      source_section: params.source_section,
    },
  });
};

/** category_click — clic en badge/categoría/uso */
export const gaCategoryClick = (params: {
  category_name: string;
  source_section: string;
  filter_type?: string;
}): void => {
  sendGAEvent({
    name: 'category_click',
    params: {
      category_name: params.category_name,
      source_section: params.source_section,
      filter_type: params.filter_type ?? 'category',
    },
  });
};
